import React, { useState, useEffect, useRef } from "react";
import { Upload, RefreshCw, Edit, Plus } from "lucide-react";
import { useLocation } from "react-router-dom";
import "./Ingredients.css";
import BaseButton from "../../Common/BaseButton";
import Loader from "../../Common/Loader";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useProductContext } from "../../Context/ProductContext";
import { manageAgentSessionIds } from "../../../helpers/index";
import { generateProductDescriptionStream } from "../../../utils/Product";

const Ingredients = ({
  generatedContent,
  loading,
  isNewSection,
  language,
  locationFocus,
  keywords: propKeywords,
  agentId: propAgentId,
  sessionId: propSessionId,
  previewMode,
  onRegenerate,
}) => {
  const items = [];

  const [title, setTitle] = useState();
  const [ingredients, setIngredients] = useState([]);
  const [error, setError] = useState(null);
  const [previousResponse, setPreviousResponse] = useState("");
  const isRegeneratingRef = useRef(false);
  const lastProcessedLengthRef = useRef(0);
  const location = useLocation();
  const initialSku = location.state?.sku || "";
  const keywords = propKeywords || location.state?.keywords || [];
  const { agentId: hookAgentId, sessionId: hookSessionId } =
    manageAgentSessionIds(initialSku);
  const agentId = propAgentId || hookAgentId;
  const sessionId = propSessionId || hookSessionId;
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasInitialData, setHasInitialData] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [editData, setEditData] = useState({});
  const { productData } = useProductContext();
  const componentType = "detailed_ingredients";

  useEffect(() => {
    const data =
      productData[initialSku]?.detailed_ingredients || generatedContent;
    if (data) {
      setIngredients(data || []);
    }
  }, [generatedContent, productData, initialSku]);

  // Fallback to default data if no API response after a certain time
  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        !hasInitialData &&
        !loading &&
        !isLoading &&
        ingredients.length === 0
      ) {
        setIngredients(items);
        setHasInitialData(true);
      }
    }, 10000); // 10 seconds timeout

    return () => clearTimeout(timer);
  }, [hasInitialData, loading, isLoading, ingredients.length, items]);

  const handleProductRegenerate = async (section, formData) => {
    if (isRegeneratingRef.current) {
      console.log(
        "Ingredients: Regeneration already in progress, skipping duplicate call"
      );
      return;
    }

    // Process formData to extract furtherDetails
    let furtherDetailsToSend = "";
    if (typeof formData === "object" && formData !== null) {
      if (formData.reason?.trim()) {
        furtherDetailsToSend = formData.reason;
      }
    } else if (typeof formData === "string") {
      furtherDetailsToSend = formData;
    }

    try {
      isRegeneratingRef.current = true;
      console.log("Ingredients: Starting regeneration for section:", section);
      setIsLoading(true);
      setIngredients([]);
      setHasInitialData(false);
      lastProcessedLengthRef.current = 0;

      const languageValue = language?.label || language || "";
      const locationValue = locationFocus?.label || locationFocus || "";

      await generateProductDescriptionStream(
        initialSku,
        keywords || [],
        "detailed_ingredients",
        furtherDetailsToSend,
        agentId,
        sessionId,
        (chunk) => {
          console.log(
            "Ingredients: Received chunk during regeneration:",
            chunk
          );
          setIngredients((prev) => {
            // Deduplicate on the fly
            const newItem = chunk;
            if (
              newItem.ingredient_name &&
              !prev.some(
                (item) => item.ingredient_name === newItem.ingredient_name
              )
            ) {
              return [...prev, newItem];
            }
            return prev;
          });
          setIsLoading(false);
        },
        () => {
          console.log("Ingredients: Regeneration streaming complete");
          setHasInitialData(true);
          isRegeneratingRef.current = false;
        },
        (err) => {
          console.error("Ingredients: Regeneration error:", err);
          setError(err.message || "Streaming error");
          setIsLoading(false);
          isRegeneratingRef.current = false;
        },
        previousResponse,
        languageValue,
        locationValue
      );
    } catch (error) {
      console.error("Ingredients: Regeneration error:", error);
      setError(error.message || "Failed to load ingredients");
      setIsLoading(false);
      isRegeneratingRef.current = false;
    }
  };

  const addIngredient = () => {
    const newIngredient = {
      id: Date.now(),
      ingredient_name: "",
      description: "",
      nutrition_facts: "",
    };
    const updatedIngredients = [newIngredient, ...ingredients];
    setIngredients(updatedIngredients);
    setEditIdx(ingredients.length); // Set edit mode for the new ingredient
    setEditData(newIngredient);
    updatePreviousResponse(updatedIngredients);
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleEdit = (idx) => {
    setEditIdx(idx);
    setEditData(ingredients[idx]);
  };

  const handleChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleSave = (idx) => {
    const updatedIngredients = ingredients.map((ingredient, index) =>
      index === idx ? editData : ingredient
    );
    setIngredients(updatedIngredients);
    setEditIdx(null);
    updatePreviousResponse(updatedIngredients);
  };

  const updatePreviousResponse = (newIngredients) => {
    if (newIngredients && newIngredients.length > 0) {
      setPreviousResponse(JSON.stringify(newIngredients));
    }
  };

  useEffect(() => {
    if (ingredients.length > 0) {
      updatePreviousResponse(ingredients);
    }
  }, [ingredients]);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        {!previewMode && (
          <>
            <h2 className="section-title">Detailed Ingredients</h2>
            {!loading &&
              (isEditing ? (
                <button className="btn btn-dark btn-sm" onClick={toggleEdit}>
                  Done
                </button>
              ) : (
                <div className="d-flex gap-2 m-2 mb-3 mr-0">
                  <BaseButton
                    className="outlineprimary"
                    children="Regenerate Text"
                    imageSrc="/regenerate-text.svg"
                    isModalOpen={true}
                    handleClick={(formData) =>
                      handleProductRegenerate("detailed_ingredients", formData)
                    }
                  />
                  <BaseButton
                    className="outlineprimary"
                    children="Edit Text"
                    imageSrc="/Edit.svg"
                    handleClick={toggleEdit}
                  />
                </div>
              ))}
          </>
        )}
      </div>

      {loading || isLoading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            padding: "20px",
            border: "1px solid #f5c2c7",
            borderRadius: "10px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#fff",
          }}
        >
          <div style={{ flex: 2 }}>
            <Skeleton
              height={35}
              width="70%"
              style={{ marginBottom: "65px" }}
            />
            <Skeleton
              count={2}
              height={12}
              width="90%"
              style={{ marginBottom: "8px" }}
            />
            <Skeleton
              count={2}
              height={12}
              width="90%"
              style={{ marginBottom: "8px" }}
            />
            <Skeleton
              count={2}
              height={12}
              width="90%"
              style={{ marginBottom: "8px" }}
            />
            <Skeleton height={12} width="80%" />
          </div>
          <div>
            <Skeleton
              height={400}
              width={300}
              style={{
                flex: 1,
                height: "400px",
                width: "100px",
                borderRadius: "8px",
                border: "1px solid #f5c2c7",
                background: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          <strong>Error:</strong> {error}
          <button
            className="btn btn-outline-danger btn-sm ms-3"
            onClick={() => {
              setError(null);
              handleProductRegenerate("detailed_ingredients", "");
            }}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {isEditing && !previewMode ? (
            <div className="sectionbgc p-3 rounded overflow-x-auto">
              <div className="mb-4 ">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="mb-4 ">
                <label className="form-label d-block">Ingredients</label>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={addIngredient}
                >
                  <Plus size={16} className="me-1" />
                  Add Ingredient
                </button>
              </div>

              <div className="product-ingredient-card-container mt-5">
                {ingredients.map((ingredient, idx) => (
                  <div
                    key={ingredient.id || idx}
                    className="product-ingredient-card"
                  >
                    {/* <label className="form-label">Ingredient card {idx + 1}</label> */}

                    {editIdx === idx ? (
                      <>
                        <div>
                          <input
                            value={editData.ingredient_name}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                ingredient_name: e.target.value,
                              })
                            }
                            placeholder="Ingredient Name"
                            className="form-control mb-2"
                          />
                          <input
                            value={editData.description}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                description: e.target.value,
                              })
                            }
                            placeholder="Description"
                            className="form-control mb-2"
                          />
                          <input
                            value={editData.nutrition_facts}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                nutrition_facts: e.target.value,
                              })
                            }
                            placeholder="Nutrition Facts"
                            className="form-control mb-2"
                          />
                        </div>
                        <div className="button-group mb-0">
                          <button
                            className="btn btn-dark btn-sm d-inline-flex"
                            onClick={() => handleSave(idx)}
                          >
                            Save
                          </button>
                          <button
                            className="btn d-inline-flex align-items-center outlineprimary btn-sm"
                            onClick={() => setEditIdx(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Image Container */}
                        <div className="product-ingredient-img">
                          {ingredient.image ? (
                            <img
                              src={ingredient.image}
                              alt={`Ingredient ${idx + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                backgroundColor: "#e0e0e0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#666",
                              }}
                            >
                              {ingredient.ingredient_name
                                ? ingredient.ingredient_name
                                    .charAt(0)
                                    .toUpperCase()
                                : "I"}
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="product-ingredient-content">
                          <h5>{ingredient.ingredient_name}</h5>
                          <p className="productDescription mb-2">
                            {ingredient.description}
                          </p>
                          <small>
                            <strong>Nutrition Facts:</strong>{" "}
                            {ingredient.nutrition_facts}
                          </small>
                        </div>
                        <button
                          className="mt-3 btn d-inline-flex align-items-center outlineprimary btn-sm"
                          onClick={() => handleEdit(idx)}
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="product-ingredient-container">
              <div className="product-ingredient-text">
                <p className="product-ingredient-title">{title}</p>
              </div>

              <div className="product-ingredient-card-container">
                {ingredients.length > 0 ? (
                  ingredients.map((ingredient, idx) => (
                    <div
                      key={ingredient.id || idx}
                      className="product-ingredient-card"
                    >
                      {/* Image Container */}
                      <div className="product-ingredient-img">
                        {ingredient.image ? (
                          <img
                            src={ingredient.image}
                            alt={`Ingredient ${idx + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              backgroundColor: "#e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#666",
                            }}
                          >
                            {ingredient.ingredient_name
                              ? ingredient.ingredient_name
                                  .charAt(0)
                                  .toUpperCase()
                              : "I"}
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="product-ingredient-content">
                        <h5>{ingredient.ingredient_name}</h5>
                        <p className="productDescription mb-2">
                          {ingredient.description}
                        </p>
                        <small>
                          <strong>Nutrition Facts:</strong>{" "}
                          {ingredient.nutrition_facts}
                        </small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center w-100">
                    <p>
                      No ingredients data available. Please regenerate or add
                      ingredients manually.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          {isLoading && (
            <div className="streaming-indicator">
              <Loader /> {/* or just: */} Streaming ingredients...
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Ingredients;
